import React, { useState } from 'react';
import type { TypeDefaults, TypeDefaultsField } from '@components/ui/cards/TypeDefaultsPreview';
import { Chip } from '@jotpsych/ui';
import {
  alpha,
  Box,
  Button,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { CleanupTypeVersion, DivergentGroup, GroupResolution } from '../types';
import {
  flagTitle,
  formatLastUsed,
  groupArchiveSuggestion,
  lastUsedLabel,
  suggestionRecencyCaution,
  templateOutliers,
  visitSemanticMismatch,
} from '../utils/versionSignals';

export interface ResolveDivergentGroupProps {
  group: DivergentGroup;
  /**
   * 'divergent' (default): same-named groups from the promotion dry-run.
   * 'personalPair' (Step 2½ lane 1): clusters of personal types on personal
   * templates — resolving promotes the winning type⇄template PAIR together.
   */
  variant?: 'divergent' | 'personalPair';
  /** Position label, e.g. "group 3 of 18". */
  groupIndex?: number;
  groupCount?: number;
  /** Controlled winner selection; pre-select the most-used, never auto-apply. */
  selectedWinnerId: string | null;
  onWinnerChange: (versionId: string) => void;
  /**
   * Merge under the (possibly renamed) shared name. In fold-in mode (group
   * already has an org canonical) winnerId is always the canonical and
   * adoptDefaultsFromId optionally copies a personal version's defaults
   * onto it — the canonical is never demoted.
   */
  onMakeShared: (resolution: {
    winnerId: string;
    sharedName: string;
    adoptDefaultsFromId?: string;
    /** Template outliers set aside — excluded from the merge, to rename. */
    setAsideIds?: string[];
  }) => void;
  onKeepSeparate: () => void;
  /** Archive every version — don't merge, don't keep. */
  onArchiveAll: () => void;
  onDecideLater: () => void;
  /**
   * Previously stored resolution for this group (back-navigation): seeds
   * the local shared-name / adopt-defaults / set-aside state so revisiting
   * a resolved group doesn't silently reset the admin's customizations.
   */
  initialResolution?: GroupResolution;
  /**
   * How many OTHER shared types use the current winner's template — shown
   * as a hint that the loose-ends step will offer consolidation later.
   */
  templateReuseHint?: number;
  disabled?: boolean;
}

const templateChipColor = (
  scope: 'user' | 'org' | 'public' | null
): 'warning' | 'success' | 'default' => {
  if (scope === 'user') return 'warning';
  if (scope === 'org' || scope === 'public') return 'success';
  return 'default';
};

const DIFF_COLUMNS: Record<
  TypeDefaultsField,
  { label: string; value: (d: TypeDefaults) => string | null }
> = {
  template: { label: 'Template', value: (d) => d.templateName },
  duration: { label: 'Duration', value: (d) => d.durationLabel },
  modality: { label: 'Modality', value: (d) => d.modalityLabel },
  placeOfService: {
    label: 'Place of service',
    value: (d) => d.placeOfServiceLabel ?? d.placeOfServiceCode,
  },
  cptCodes: {
    label: 'CPT',
    value: (d) => (d.cptCodes.length ? d.cptCodes.join(', ') : null),
  },
  forms: {
    label: 'Forms',
    value: (d) => (d.forms.length ? `${d.forms.length} scheduled` : null),
  },
  reminders: {
    label: 'Reminders',
    value: (d) =>
      d.reminders.length ? `${d.reminders.length} scheduled` : null,
  },
};

/**
 * The fields that genuinely differ across the group, rendered as aligned
 * columns on every card so differences can be scanned vertically. Unset
 * values render as "—" so absence is visible too.
 */
const DiffGrid: React.FC<{
  defaults: TypeDefaults;
  fields: TypeDefaultsField[];
  showLabels: boolean;
}> = ({ defaults, fields, showLabels }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))`,
      columnGap: 1.5,
      mt: 0.5,
    }}
  >
    {fields.map((field) => {
      const column = DIFF_COLUMNS[field];
      const value = column.value(defaults);
      return (
        <Box key={field} sx={{ minWidth: 0 }}>
          {showLabels && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', fontSize: 10, lineHeight: 1.4 }}
            >
              {column.label}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: value ? 'warning.dark' : 'text.disabled',
              fontWeight: value ? 600 : 400,
              overflowWrap: 'anywhere',
            }}
          >
            {value ?? '—'}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/** Meta row shared by both card styles: name, usage, recency, flags. */
const VersionMeta: React.FC<{
  version: CleanupTypeVersion;
  group: DivergentGroup;
  mostUsed: boolean;
  showLabels: boolean;
}> = ({ version, group, mostUsed, showLabels }) => {
  const templateIsDiff = group.diffFields.includes('template');
  return (
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {version.provider ??
            (version.ownerScope === 'organization'
              ? 'Practice-wide'
              : 'Unassigned')}
        </Typography>
        {version.ownerScope === 'organization' && (
          <Chip
            size="xSmall"
            variant="soft"
            color="success"
            label="already the shared version"
          />
        )}
        {mostUsed && (
          <Chip
            size="xSmall"
            variant="soft"
            color="info"
            label={`most used · ${version.usage} visits`}
          />
        )}
        {!mostUsed && version.usage > 0 && (
          <Typography variant="caption" color="text.secondary">
            {version.usage} visits
          </Typography>
        )}
        {version.lastUsedAt && (
          <Typography variant="caption" color="text.secondary">
            · {lastUsedLabel(version.lastUsedAt)}
          </Typography>
        )}
        {!version.templateId && (
          <Chip
            size="xSmall"
            variant="soft"
            color="warning"
            label="no template set"
          />
        )}
        {flagTitle(version.templateTitle) && (
          <Chip
            size="xSmall"
            variant="soft"
            color="error"
            label={`template ${flagTitle(version.templateTitle)}`}
          />
        )}
        {/* When template isn't a diff column, show it here (pair context). */}
        {!templateIsDiff && version.templateTitle && (
          <Chip
            size="xSmall"
            variant="soft"
            color={templateChipColor(version.templateScope)}
            label={
              version.templateScope === 'user'
                ? `${version.templateTitle} · personal`
                : version.templateTitle
            }
          />
        )}
        {templateIsDiff && version.templateScope === 'user' && (
          <Chip size="xSmall" variant="soft" color="warning" label="personal template" />
        )}
      </Stack>
      {group.diffFields.length > 0 && (
        <DiffGrid
          defaults={version.defaults}
          fields={group.diffFields}
          showLabels={showLabels}
        />
      )}
    </Box>
  );
};

/**
 * Wizard Step 2 (GDN-2237): one same-named divergent group, resolved by the
 * admin. Hybrid model: each version is a template⇄type pair; picking a
 * winner and "making it the shared pair" merges the types, re-links them to
 * the winning template, and frees the losing templates. When the group
 * already contains the org's shared row, the card switches to FOLD-IN mode:
 * the canonical is pinned (not a choice), personal copies merge into it,
 * and adopting a copy's defaults is an explicit per-copy action — the
 * shared type is never demoted. Pure presentation.
 */
export const ResolveDivergentGroup: React.FC<ResolveDivergentGroupProps> = ({
  group,
  variant = 'divergent',
  groupIndex,
  groupCount,
  selectedWinnerId,
  onWinnerChange,
  onMakeShared,
  onKeepSeparate,
  onArchiveAll,
  onDecideLater,
  initialResolution,
  templateReuseHint = 0,
  disabled = false,
}) => {
  const stored =
    initialResolution?.kind === 'make_shared' ? initialResolution : undefined;
  const isPersonalPair = variant === 'personalPair';
  const singleProvider =
    new Set(group.versions.map((v) => v.provider ?? 'org')).size === 1;
  const canonical =
    (!isPersonalPair &&
      group.versions.find((v) => v.ownerScope === 'organization')) ||
    null;
  const foldIn = canonical !== null;

  // Local because it's ephemeral UI state; parent remounts per group (key)
  // and seeds from the stored resolution when revisiting.
  const [sharedName, setSharedName] = useState(
    stored?.sharedName ?? group.displayName
  );
  const [adoptFromId, setAdoptFromId] = useState<string | null>(
    stored?.adoptDefaultsFromId ?? null
  );
  const [setAsideIds, setSetAsideIds] = useState<ReadonlySet<string>>(
    new Set(stored?.setAsideIds ?? [])
  );

  // Fold-in copies fold into the canonical by definition — outlier
  // detection applies to the pick-a-winner variants.
  const outlierInfo = foldIn ? null : templateOutliers(group.versions);
  const pendingOutliers =
    outlierInfo?.outliers.filter((v) => !setAsideIds.has(v.id)) ?? [];
  const setAsideCount = setAsideIds.size;
  const visible = group.versions.filter((v) => !setAsideIds.has(v.id));

  const copies = foldIn
    ? visible.filter((v) => v.id !== canonical.id)
    : visible;
  const adoptFrom = copies.find((v) => v.id === adoptFromId) ?? null;

  const recencyCaution = foldIn ? null : suggestionRecencyCaution(visible);
  const archiveSuggestion = groupArchiveSuggestion(visible);
  const nameFlag = flagTitle(group.displayName);
  const winner = foldIn
    ? canonical
    : (visible.find((v) => v.id === selectedWinnerId) ?? null);
  const losers = visible.filter((v) => v.id !== winner?.id);
  const effectiveName = foldIn
    ? group.displayName
    : sharedName.trim() || group.displayName;
  const pairMismatch = winner
    ? visitSemanticMismatch(effectiveName, winner.templateTitle)
    : null;
  const freedTemplates = winner
    ? [
        ...new Set(
          losers
            .filter((v) => v.templateScope === 'user' && v.templateTitle)
            .filter((v) => v.templateTitle !== winner.templateTitle)
            .map((v) => v.templateTitle as string)
        ),
      ]
    : [];

  const setAsideOutliers = (): void => {
    const ids = new Set(setAsideIds);
    for (const v of pendingOutliers) ids.add(v.id);
    setSetAsideIds(ids);
    // The suggested winner can itself be an outlier — reselect if so.
    if (selectedWinnerId && ids.has(selectedWinnerId)) {
      const fallback = group.versions.find((v) => !ids.has(v.id));
      if (fallback) onWinnerChange(fallback.id);
    }
  };

  const mergeButtonLabel = foldIn
    ? `Merge ${copies.length} ${copies.length === 1 ? 'copy' : 'copies'} into shared type`
    : isPersonalPair
      ? 'Promote pair for the practice'
      : 'Merge into shared type';

  return (
    <Box
      sx={(theme) => ({
        // Personal copies are a different question (opt-in sharing) — the
        // blue tint marks the change of territory from the shared-catalog
        // lanes' warm tint.
        border: `1px solid ${alpha(
          isPersonalPair ? theme.palette.info.main : theme.palette.warning.main,
          0.35
        )}`,
        borderRadius: 2,
        bgcolor: alpha(
          isPersonalPair ? theme.palette.info.main : theme.palette.warning.main,
          0.03
        ),
        p: 2,
      })}
    >
      <Box sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 0 }}>
            &ldquo;{group.displayName}&rdquo;
          </Typography>
          {groupIndex !== undefined && groupCount !== undefined && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ whiteSpace: 'nowrap' }}
            >
              group {groupIndex + 1} of {groupCount}
            </Typography>
          )}
        </Box>
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          sx={{ flexWrap: 'wrap', mt: 0.5 }}
        >
          <Chip
            size="xSmall"
            variant="soft"
            color="warning"
            label={
              isPersonalPair
                ? `${group.versions.length} personal copies · each on a personal template`
                : foldIn
                  ? `shared type exists · ${copies.length} personal ${copies.length === 1 ? 'copy' : 'copies'} to fold in`
                  : `${group.versions.length} versions · templates differ`
            }
          />
          {nameFlag && !foldIn && (
            <Chip
              size="xSmall"
              variant="soft"
              color="error"
              label={`name ${nameFlag} — consider renaming below`}
            />
          )}
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {isPersonalPair
          ? singleProvider
            ? 'This provider keeps more than one copy of this type, each on ' +
              'its own personal template. Promote one pair to share it with ' +
              'the practice — the winning template comes along and the other ' +
              'copies merge in — or keep them personal.'
            : 'Several providers keep their own copy of this type, each wired ' +
              'to a personal template. Pick the pair the practice should ' +
              'share — the winning template is promoted along with the type, ' +
              'and the other copies merge into it.'
          : foldIn
            ? 'Your practice already shares this type. The copies below ' +
              'fold into it — providers keep per-visit overrides. If a ' +
              "copy's defaults reflect how the practice actually works, " +
              'adopt them into the shared type first.'
            : 'These share a name but differ where it matters. Pick the ' +
              'version your practice should share — providers keep ' +
              'per-visit overrides either way.'}
      </Typography>

      {archiveSuggestion && (
        <Box
          sx={(theme) => ({
            mb: 1.5,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.info.main, 0.45)}`,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            px: 1.5,
            py: 1,
          })}
        >
          <Typography variant="caption" sx={{ color: 'info.dark' }}>
            💡 No provider has used any version of this{' '}
            {archiveSuggestion.newestUse
              ? `since ${formatLastUsed(archiveSuggestion.newestUse)}`
              : 'at all'}
            {nameFlag && ` — and the name ${nameFlag}`}. Archiving all of them
            may be better than merging: they stay on past notes and can be
            restored anytime.
          </Typography>
        </Box>
      )}

      {pendingOutliers.length > 0 && (
        <Box
          sx={(theme) => ({
            mb: 1.5,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.45)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          })}
        >
          <Typography variant="caption" sx={{ color: 'warning.dark', flexGrow: 1 }}>
            🏷 {pendingOutliers.length} of these point at a{' '}
            <b>different template</b> than the rest (
            {pendingOutliers
              .slice(0, 2)
              .map((v) => `${v.provider ?? 'unassigned'} → ${v.templateTitle ?? 'no template'}`)
              .join('; ')}
            {pendingOutliers.length > 2 && '; …'}) — they may be misnamed
            rather than duplicates. Merging would rewire{' '}
            {pendingOutliers.length === 1 ? 'that workflow' : 'those workflows'}.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={disabled}
            onClick={setAsideOutliers}
            sx={{ flexShrink: 0 }}
          >
            Set {pendingOutliers.length === 1 ? 'it' : 'them'} aside
          </Button>
        </Box>
      )}

      {setAsideCount > 0 && (
        <Box
          sx={(theme) => ({
            mb: 1.5,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          })}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            {setAsideCount} set aside — kept out of this merge; you&apos;ll
            rename {setAsideCount === 1 ? 'it' : 'them'} on the Loose ends step.
          </Typography>
          <Button
            size="small"
            variant="text"
            color="inherit"
            disabled={disabled}
            onClick={() => setSetAsideIds(new Set())}
          >
            Undo
          </Button>
        </Box>
      )}

      {!archiveSuggestion && recencyCaution && (
        <Box
          sx={(theme) => ({
            mb: 1.5,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.45)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            px: 1.5,
            py: 1,
          })}
        >
          <Typography variant="caption" sx={{ color: 'warning.dark' }}>
            ⚠️ The most-used version hasn&apos;t been used since{' '}
            {formatLastUsed(recencyCaution.suggested.lastUsedAt) ?? 'a while'},
            but {recencyCaution.fresher.provider ?? 'another provider'}&apos;s
            was used {formatLastUsed(recencyCaution.fresher.lastUsedAt)} — the
            practice may have moved on. Double-check before merging.
          </Typography>
        </Box>
      )}

      {foldIn && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.success.main, 0.05),
            p: 1.25,
            mb: 1,
          })}
        >
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: 'success.dark', lineHeight: 1.5 }}
            >
              Your practice&apos;s shared version
            </Typography>
            <VersionMeta
              version={canonical}
              group={group}
              mostUsed={group.versions[0]?.id === canonical.id}
              showLabels
            />
            {adoptFrom && (
              <Box sx={{ mt: 0.75 }}>
                <Chip
                  size="xSmall"
                  variant="soft"
                  color="info"
                  label={`will adopt ${adoptFrom.provider ?? 'the selected copy'}'s defaults on merge`}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Stack spacing={1} sx={{ mb: 1.5 }}>
        {copies.map((version, index) => {
          const isSelected = !foldIn && version.id === selectedWinnerId;
          const isAdopted = foldIn && version.id === adoptFromId;
          return (
            <Box
              key={version.id}
              onClick={() => !foldIn && !disabled && onWinnerChange(version.id)}
              sx={(theme) => ({
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                border: `1px solid ${
                  isSelected || isAdopted
                    ? theme.palette.primary.main
                    : theme.palette.divider
                }`,
                borderRadius: 1.5,
                bgcolor:
                  isSelected || isAdopted
                    ? alpha(theme.palette.primary.main, 0.04)
                    : theme.palette.background.paper,
                p: 1.25,
                cursor: foldIn || disabled ? 'default' : 'pointer',
              })}
            >
              {!foldIn && (
                <Radio
                  size="small"
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => onWinnerChange(version.id)}
                  sx={{ p: 0.25, mt: '-1px' }}
                  slotProps={{
                    input: {
                      'aria-label': `Use ${version.provider ?? 'this'} version of ${group.displayName}`,
                    },
                  }}
                />
              )}
              <VersionMeta
                version={version}
                group={group}
                mostUsed={!foldIn && index === 0}
                showLabels={index === 0}
              />
              {foldIn && (
                <Button
                  size="small"
                  variant={isAdopted ? 'contained' : 'text'}
                  disabled={disabled}
                  onClick={() =>
                    setAdoptFromId(isAdopted ? null : version.id)
                  }
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {isAdopted ? 'Adopting defaults ✓' : 'Adopt these defaults'}
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>

      {templateReuseHint > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1.5 }}
        >
          ⓘ This template also serves {templateReuseHint} other shared{' '}
          {templateReuseHint === 1 ? 'type' : 'types'} — if any end up
          interchangeable after merging, the <b>Loose ends</b> step will
          offer to consolidate them. No need to solve that here.
        </Typography>
      )}

      {winner && !foldIn && (
        <TextField
          size="small"
          fullWidth
          label="Share as"
          value={sharedName}
          disabled={disabled}
          onChange={(e) => setSharedName(e.target.value)}
          helperText={
            sharedName.trim() !== group.displayName
              ? `Will be renamed from "${group.displayName}" for everyone`
              : 'Rename it here if the current name is messy — defaults can be adjusted after merging'
          }
          sx={{ mb: 1.5 }}
        />
      )}

      {winner && freedTemplates.length > 0 && (
        <Box
          sx={(theme) => ({
            mb: 1.5,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            bgcolor: alpha(theme.palette.success.main, 0.06),
            px: 1.5,
            py: 1,
          })}
        >
          <Typography variant="caption" sx={{ color: 'success.dark' }}>
            🧹 If merged, {freedTemplates.length === 1 ? 'this template' : 'these templates'}{' '}
            will no longer be used by any type — we&apos;ll offer to archive{' '}
            {freedTemplates.length === 1 ? 'it' : 'them'} at the end:{' '}
            {freedTemplates.join(' · ')}
          </Typography>
        </Box>
      )}

      {/* Sticky decision bar: summary + actions stay visible while the
          version list scrolls, so the resolution buttons are never lost
          below the fold. */}
      <Box
        sx={(theme) => ({
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          boxShadow: theme.shadows[3],
          p: 1.5,
        })}
      >
      {/* What-will-happen summary: the pair spelled out in full, so the
          action buttons can stay short and fixed-width. */}
      {winner && (
        <Box
          sx={(theme) => ({
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
            bgcolor: alpha(theme.palette.success.main, 0.05),
            px: 1.5,
            py: 1,
            mb: 1.5,
          })}
        >
          <Typography variant="caption" sx={{ color: 'success.dark' }}>
            {foldIn ? (
              <>
                Merging folds <b>{copies.length}</b>{' '}
                {copies.length === 1 ? 'copy' : 'copies'} into{' '}
                <b>&ldquo;{group.displayName}&rdquo;</b>
                {adoptFrom
                  ? ` — adopting ${adoptFrom.provider ?? 'the selected copy'}'s defaults`
                  : ' — keeping the current shared defaults'}
                .
              </>
            ) : (
              <>
                {isPersonalPair ? 'Promoting' : 'Sharing'}:{' '}
                <b>&ldquo;{effectiveName}&rdquo;</b>
                {winner.templateTitle && (
                  <>
                    {' '}
                    ⇄ {winner.templateTitle}
                    {winner.templateScope === 'user' &&
                      ' (template promoted too)'}
                  </>
                )}{' '}
                — {winner.provider ?? 'the practice-wide'}
                {winner.provider ? "'s version" : ' version'}
                {losers.length > 0 &&
                  `; ${losers.length} ${losers.length === 1 ? 'copy merges' : 'copies merge'} in`}
                .
              </>
            )}
          </Typography>
          {pairMismatch && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'warning.dark', mt: 0.25 }}
            >
              ⚠️ Type and template names disagree — the type reads like{' '}
              {pairMismatch.typeCategory === 'intake'
                ? 'an intake'
                : 'a follow-up'}{' '}
              but the template like{' '}
              {pairMismatch.templateCategory === 'intake'
                ? 'an intake'
                : 'a follow-up'}
              . Double-check this pair before sharing it.
            </Typography>
          )}
        </Box>
      )}

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Button
          variant={
            archiveSuggestion || isPersonalPair ? 'outlined' : 'contained'
          }
          color="success"
          size="small"
          disabled={
            disabled || !winner || (!foldIn && !sharedName.trim())
          }
          onClick={() =>
            winner &&
            onMakeShared({
              winnerId: winner.id,
              sharedName: effectiveName,
              adoptDefaultsFromId: adoptFromId ?? undefined,
              setAsideIds: setAsideCount > 0 ? [...setAsideIds] : undefined,
            })
          }
        >
          {winner ? mergeButtonLabel : 'Pick a version to share'}
        </Button>
        <Button
          variant={
            isPersonalPair && !archiveSuggestion ? 'contained' : 'outlined'
          }
          size="small"
          disabled={disabled}
          onClick={onKeepSeparate}
        >
          {isPersonalPair ? 'Keep personal' : 'Keep separate'}
        </Button>
        <Button
          variant={archiveSuggestion ? 'contained' : 'outlined'}
          color="warning"
          size="small"
          disabled={disabled}
          onClick={() => {
            // "All" includes anything set aside — clear the queue so the
            // strip doesn't promise a rename that won't happen.
            setSetAsideIds(new Set());
            onArchiveAll();
          }}
        >
          Archive all {group.versions.length}
        </Button>
        <Button
          variant="text"
          size="small"
          color="inherit"
          disabled={disabled}
          onClick={onDecideLater}
          sx={{ ml: 'auto' }}
        >
          Decide later
        </Button>
      </Stack>
      </Box>
    </Box>
  );
};
